import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { MessageSquare, X } from "lucide-react";

export default function AdminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [adminReply, setAdminReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchAllTickets();
  }, [filterStatus]);

  const fetchAllTickets = async () => {
    try {
      setLoading(true);
      const statusParam = filterStatus === 'all' ? '' : `?status=${filterStatus}`;
      const response = await axios.get(`${API}/admin/tickets${statusParam}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (error.response?.status === 403) {
        toast.error('Нет доступа. Требуются права администратора.');
      } else {
        toast.error('Ошибка загрузки тикетов');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`);
      setSelectedTicket(response.data.ticket);
      setTicketMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Ошибка загрузки тикета');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!adminReply.trim()) {
      toast.error('Введите сообщение');
      return;
    }

    try {
      await axios.post(`${API}/tickets/${selectedTicket.id}/messages`, {
        message: adminReply
      });

      setAdminReply('');
      fetchTicketDetails(selectedTicket.id);
      toast.success('Ответ отправлен');
      
      // Refresh tickets list to update message count
      fetchAllTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Ошибка отправки ответа');
    }
  };

  const handleChangeStatus = async (status) => {
    if (!selectedTicket) return;

    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}/status`, {
        status: status
      });

      toast.success(`Статус изменен на: ${status}`);
      setSelectedTicket({ ...selectedTicket, status });
      fetchAllTickets();
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('Ошибка изменения статуса');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'open': <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">Открыт</span>,
      'in_progress': <span className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-800 font-semibold">В работе</span>,
      'closed': <span className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-800 font-semibold">Закрыт</span>
    };
    return badges[status] || status;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'low': <span className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600 font-semibold">Низкий</span>,
      'medium': <span className="px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 font-semibold">Средний</span>,
      'high': <span className="px-3 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">Высокий</span>
    };
    return badges[priority] || priority;
  };

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
        <Card className="border-2 border-black">
          <CardHeader>
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div className="flex-1">
                <Button
                  onClick={() => {
                    setSelectedTicket(null);
                    setTicketMessages([]);
                    setAdminReply('');
                  }}
                  variant="outline"
                  className="mb-3 border-2 border-black"
                >
                  ← Назад к списку
                </Button>
                <CardTitle className="text-xl md:text-2xl mt-2">
                  {selectedTicket.subject}
                </CardTitle>
                <CardDescription className="mt-3 flex flex-wrap gap-2">
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                  <span className="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-800 font-semibold">
                    👤 {selectedTicket.user_email}
                  </span>
                </CardDescription>
              </div>
              
              {/* Status Change Buttons */}
              <div className="flex flex-wrap gap-2">
                {selectedTicket.status !== 'open' && (
                  <Button
                    onClick={() => handleChangeStatus('open')}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Открыть
                  </Button>
                )}
                {selectedTicket.status !== 'in_progress' && (
                  <Button
                    onClick={() => handleChangeStatus('in_progress')}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    В работу
                  </Button>
                )}
                {selectedTicket.status !== 'closed' && (
                  <Button
                    onClick={() => handleChangeStatus('closed')}
                    size="sm"
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    Закрыть
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Messages */}
            <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
              {ticketMessages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Нет сообщений</p>
              ) : (
                ticketMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg border-l-4 ${
                      msg.author_type === 'admin'
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-gray-50 border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${msg.author_type === 'admin' ? 'text-blue-700' : 'text-gray-700'}`}>
                          {msg.author_type === 'admin' ? '👨‍💼 Поддержка' : '👤 Пользователь'}
                        </span>
                        <span className="text-xs text-gray-500">({msg.author_email})</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(msg.created_at).toLocaleString('ru-RU')}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))
              )}
            </div>

            {/* Reply Form */}
            <form onSubmit={handleSendReply} className="border-t-2 border-black pt-6">
              <Label htmlFor="admin-reply" className="text-lg font-bold">Ответ администратора</Label>
              <textarea
                id="admin-reply"
                value={adminReply}
                onChange={(e) => setAdminReply(e.target.value)}
                className="w-full mt-2 p-3 border-2 border-black rounded-md focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder="Введите ваш ответ пользователю..."
                required
              />
              <div className="flex gap-2 mt-3">
                <Button
                  type="submit"
                  className="bg-black text-white hover:bg-gray-800"
                  disabled={!adminReply.trim()}
                >
                  Отправить ответ
                </Button>
                <Button
                  type="button"
                  onClick={() => setAdminReply('')}
                  variant="outline"
                  className="border-2 border-black"
                >
                  Очистить
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tickets List View
  return (
    <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
      <Card className="border-2 border-black">
        <CardHeader>
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div>
              <CardTitle className="text-xl md:text-2xl">🎫 Тикеты поддержки</CardTitle>
              <CardDescription className="mt-2">Управление обращениями пользователей</CardDescription>
            </div>
            <Button
              onClick={fetchAllTickets}
              className="bg-black text-white hover:bg-gray-800"
            >
              🔄 Обновить
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              onClick={() => setFilterStatus('all')}
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              className={`${
                filterStatus === 'all'
                  ? 'bg-black text-white'
                  : 'border-2 border-black bg-white text-black hover:bg-gray-100'
              }`}
            >
              Все ({tickets.length})
            </Button>
            <Button
              onClick={() => setFilterStatus('open')}
              variant={filterStatus === 'open' ? 'default' : 'outline'}
              className={`${
                filterStatus === 'open'
                  ? 'bg-green-600 text-white'
                  : 'border-2 border-green-600 bg-white text-green-600 hover:bg-green-50'
              }`}
            >
              Открытые
            </Button>
            <Button
              onClick={() => setFilterStatus('in_progress')}
              variant={filterStatus === 'in_progress' ? 'default' : 'outline'}
              className={`${
                filterStatus === 'in_progress'
                  ? 'bg-blue-600 text-white'
                  : 'border-2 border-blue-600 bg-white text-blue-600 hover:bg-blue-50'
              }`}
            >
              В работе
            </Button>
            <Button
              onClick={() => setFilterStatus('closed')}
              variant={filterStatus === 'closed' ? 'default' : 'outline'}
              className={`${
                filterStatus === 'closed'
                  ? 'bg-gray-600 text-white'
                  : 'border-2 border-gray-600 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Закрытые
            </Button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8 text-gray-500">
              <p>Загрузка тикетов...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && tickets.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-semibold">Нет тикетов</p>
              <p className="text-sm mt-2">
                {filterStatus === 'all' 
                  ? 'Пока нет обращений от пользователей'
                  : `Нет тикетов со статусом "${filterStatus}"`
                }
              </p>
            </div>
          )}

          {/* Tickets List */}
          {!loading && tickets.length > 0 && (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => fetchTicketDetails(ticket.id)}
                  className="p-4 border-2 border-gray-300 rounded-lg hover:border-black hover:shadow-md cursor-pointer transition-all bg-white"
                >
                  <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-lg truncate">{ticket.subject}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        📧 {ticket.user_email}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {getStatusBadge(ticket.status)}
                      {getPriorityBadge(ticket.priority)}
                    </div>
                  </div>
                  
                  {ticket.last_message && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      💬 {ticket.last_message}
                    </p>
                  )}
                  
                  <div className="flex justify-between items-center text-xs text-gray-500 flex-wrap gap-2">
                    <span>
                      💬 Сообщений: <strong className="text-black">{ticket.message_count || 0}</strong>
                    </span>
                    <span>
                      📅 {new Date(ticket.created_at).toLocaleString('ru-RU')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
